import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, Store, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useStores, useSelectedStore } from '@/store/dataStore';

export default function StoreSelector({ forceOpen = false, onClose }) {
  const { stores } = useStores();
  const { store, selectStore } = useSelectedStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!store && !forceOpen) setOpen(true);
    if (forceOpen) setOpen(true);
  }, [store, forceOpen]);

  const pick = (id) => {
    selectStore(id);
    setOpen(false);
    onClose && onClose();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.address || '').toLowerCase().includes(q) ||
      (s.city || '').toLowerCase().includes(q)
    );
  }, [stores, query]);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[85vh]">
        <DialogHeader className="p-6 pb-3 flex-shrink-0">
          <DialogTitle className="text-2xl font-extrabold text-[#1B2A4A] flex items-center gap-2">
            <Store className="text-[#F8D13F] bg-[#1B2A4A] rounded p-1" size={30} /> Elige tu tienda
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">Selecciona la sucursal más cercana. Solo verás productos disponibles en esa tienda y tu pedido irá a su WhatsApp.</p>
          {stores.length >= 3 && (
            <div className="relative mt-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, ciudad o dirección…"
                className="pl-9 h-9"
                data-testid="store-selector-search"
                autoFocus
              />
            </div>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 space-y-2" data-testid="store-selector-list">
          {filtered.map((s) => {
            const active = store?.id === s.id;
            return (
              <button
                key={s.id}
                onClick={() => pick(s.id)}
                data-testid={`store-option-${s.id}`}
                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border-2 transition ${active ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 hover:border-yellow-300'}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#F8D13F] text-[#1B2A4A] flex items-center justify-center flex-shrink-0"><MapPin size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#1B2A4A]">{s.name}</div>
                  <div className="text-xs text-slate-500 truncate">{s.address}{s.city ? ` · ${s.city}` : ''}</div>
                </div>
                {active && <Check className="text-yellow-600" />}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="store-selector-empty">
              No encontramos tiendas que coincidan con "{query}".
            </p>
          )}
        </div>
        <div className="p-6 pt-3 flex-shrink-0 border-t border-slate-100 bg-white">
          <p className="text-[11px] text-slate-400 text-center mb-2">{filtered.length} de {stores.length} tienda{stores.length !== 1 ? 's' : ''}</p>
          <Button onClick={() => setOpen(false)} disabled={!store} className="w-full bg-[#1B2A4A] hover:bg-[#141f38] text-yellow-300 font-bold rounded-full h-11">
            {store ? 'Continuar comprando' : 'Selecciona una tienda'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
